import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import RelatedBlogs from 'components/RelatedBlogs';
import { predictiveAnalyticsFAQs } from '../data/faqs';
import { useNavigate } from 'react-router-dom';

const PredictiveAnalytics = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Predictive Analytics Services - Forecasting & Modeling | OctalChip',
    description: 'Data-driven forecasting and predictive modeling to make informed business decisions. Analyze trends, predict outcomes, and optimize operations.',
    keywords: 'predictive analytics, forecasting, predictive modeling, data analytics, business intelligence, risk analysis',
    canonical: 'https://www.octalchip.com/services/ai-ml-services/predictive-analytics',
    faqData: predictiveAnalyticsFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Predictive Analytics Services",
      "description": "Data-driven forecasting and predictive modeling for business decisions",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const features = [
    { title: 'Forecasting Models', description: 'Predict future trends and values based on historical data patterns', icon: 'TrendingUp' },
    { title: 'Risk Analysis', description: 'Assess and predict risks to make informed decisions', icon: 'AlertTriangle' },
    { title: 'Demand Forecasting', description: 'Predict product demand to optimize inventory and supply chain', icon: 'ShoppingBag' },
    { title: 'Churn Prediction', description: 'Identify customers likely to churn and take preventive actions', icon: 'Users' },
    { title: 'Sales Forecasting', description: 'Predict future sales to plan resources and strategies', icon: 'DollarSign' },
    { title: 'Performance Analytics', description: 'Analyze and predict system and business performance', icon: 'BarChart' }
  ];

  const technologies = [
    { name: 'Python', category: 'Language', description: 'Primary language for analytics' },
    { name: 'R', category: 'Language', description: 'Statistical computing language' },
    { name: 'Pandas', category: 'Library', description: 'Data manipulation and analysis' },
    { name: 'NumPy', category: 'Library', description: 'Numerical computing' },
    { name: 'Prophet', category: 'Library', description: 'Time series forecasting' },
    { name: 'ARIMA', category: 'Model', description: 'Time series analysis' }
  ];

  const useCases = [
    { title: 'Sales Forecasting', description: 'Predict future sales to optimize inventory and planning', icon: 'TrendingUp' },
    { title: 'Inventory Management', description: 'Optimize stock levels based on demand predictions', icon: 'Package' },
    { title: 'Customer Churn Prediction', description: 'Identify at-risk customers and prevent churn', icon: 'UserX' },
    { title: 'Risk Assessment', description: 'Evaluate financial and operational risks', icon: 'Shield' },
    { title: 'Market Analysis', description: 'Predict market trends and opportunities', icon: 'LineChart' },
    { title: 'Operational Optimization', description: 'Optimize operations based on predictive insights', icon: 'Settings' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO title={seoData.title} description={seoData.description} keywords={seoData.keywords} canonical={seoData.canonical} structuredData={seoData.structuredData} />
      <Header />
      {/* Enhanced Hero Section */}
      <section id="hero" className="relative py-20 md:py-32 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00d690 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-2xl mb-8 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <Icon name="TrendingUp" size={32} className="text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-800 mb-4 sm:mb-6 leading-tight px-4">Predictive Analytics<span className="block text-[#00d690] mt-1 sm:mt-2">Solutions</span></h1>
            <div className="w-20 sm:w-24 md:w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-8"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">Data-driven forecasting and predictive modeling to make informed business decisions. Analyze trends, predict outcomes, and optimize operations.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="primary" onClick={() => navigate('/services#share-your-requirements')} className="px-10 py-4 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all">Start Your Project</Button>
              <Button variant="outline" onClick={() => navigate('/ai-ml-services')} className="px-10 py-4 text-lg border-2">View All AI Services</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-6 sm:py-8 md:py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">18+</div>
              <div className="text-gray-600 font-medium">Analytics Projects</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">3-8</div>
              <div className="text-gray-600 font-medium">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">90%+</div>
              <div className="text-gray-600 font-medium">Accuracy Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Key Features</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-3 sm:mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive features designed to deliver exceptional predictive analytics solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-xl flex items-center justify-center mb-3 sm:mb-4 sm:mb-6 group-hover:from-[#00d690] group-hover:to-[#00b876] transition-all duration-300">
                  <Icon name={feature.icon} size={20} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Technologies We Master</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-3 sm:mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              We work with the latest and most powerful analytics technologies to build accurate predictive models
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-7xl mx-auto">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">{tech.name}</span>
                  <span className="text-xs font-medium text-[#00d690] bg-[#00d690]/10 px-2 py-1 rounded">{tech.category}</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#00d690]/5 to-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">What We Build</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-3 sm:mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              From sales forecasting to risk assessment, we deliver predictive analytics solutions for every business need
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group border-l-4 border-[#00d690]">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-3 sm:mb-4 sm:mb-6 group-hover:bg-[#00d690] transition-all duration-300">
                  <Icon name={useCase.icon} size={20} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Our Development Process</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-3 sm:mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              A proven methodology that ensures quality, transparency, and timely delivery
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d690] to-[#00b876]"></div>
              
              <div className="space-y-12">
                {[
                  {
                    step: '01',
                    title: 'Requirements Analysis & Data Assessment',
                    description: 'We analyze your business needs, assess available data, and identify the best predictive analytics approach for your use case',
                    icon: 'Search'
                  },
                  {
                    step: '02',
                    title: 'Data Collection & Preparation',
                    description: 'We collect, clean, and preprocess your data, handle missing values, and prepare datasets optimized for predictive modeling',
                    icon: 'Database'
                  },
                  {
                    step: '03',
                    title: 'Model Development & Training',
                    description: 'We develop custom predictive models using appropriate algorithms, train them on your data, and optimize for accuracy',
                    icon: 'Code'
                  },
                  {
                    step: '04',
                    title: 'Model Evaluation & Validation',
                    description: 'We thoroughly evaluate models using cross-validation, test on unseen data, and validate accuracy and performance metrics',
                    icon: 'CheckCircle'
                  },
                  {
                    step: '05',
                    title: 'Integration & Deployment',
                    description: 'We integrate predictive models into your systems, create dashboards and APIs, and deploy to production with monitoring',
                    icon: 'Rocket'
                  },
                  {
                    step: '06',
                    title: 'Monitoring & Continuous Improvement',
                    description: 'We continuously monitor model performance, retrain with new data, and optimize for better accuracy and insights',
                    icon: 'BarChart'
                  }
                ].map((step, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                      <div className="flex items-center mb-3 sm:mb-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                          <Icon name={step.icon} size={16} className="text-[#00d690]" />
                        </div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-lg">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Why Choose Our Predictive Analytics Services?</h2>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-3 sm:mb-4 sm:mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Expert data scientists with proven track record in predictive modeling',
                'Custom solutions tailored to your specific business needs and data',
                'End-to-end development from data preparation to production deployment',
                'Scalable analytics systems that handle large volumes of data',
                'Continuous monitoring and model optimization for better accuracy',
                'Integration with existing systems and business intelligence tools',
                'Cost-effective analytics solutions with measurable ROI',
                'Ongoing support and model maintenance'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 bg-white/5 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#00d690] rounded-full flex items-center justify-center">
                    <Icon name="Check" size={20} className="text-white" />
                  </div>
                  <p className="text-lg text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-12 md:py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 md:mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
              Ready to Build Your Predictive Analytics Solution?
            </h2>
            <div className="w-16 md:w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-3 sm:mb-4 md:mb-3 sm:mb-4 sm:mb-6"></div>
            <p className="text-base md:text-lg lg:text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Let's discuss your project requirements and create a solution that drives your business forward. 
              Get a free consultation and quote today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/services#share-your-requirements')}
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
              >
                Start Your Project
              </button>
              <button
                onClick={() => navigate('/ai-ml-services')}
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 text-sm sm:text-base"
              >
                View All AI Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={predictiveAnalyticsFAQs}
        title="Predictive Analytics FAQs"
        description="Common questions about predictive analytics, forecasting, and data modeling services."
      />

      {/* Related Blogs Section */}
      <RelatedBlogs
        servicePath="/services/ai-ml-services/predictive-analytics"
        serviceTags={['Predictive Analytics', 'Forecasting', 'Data Analytics', 'Business Intelligence', 'Risk Analysis']}
        serviceName="Predictive Analytics"
        limit={6}
        title="Predictive Analytics Related Blogs"
      />

      <Footer />
    </div>
  );
};

export default PredictiveAnalytics;

